import React, { useEffect, useCallback } from 'react'
import { useFormContext, Controller } from 'react-hook-form'
import { Box, Grid, Typography, useTheme } from '@mui/material'
import { get } from 'utils/lodash'
import EventInstanceTypeRadioGroup from 'components/configured-service/common/EventInstanceTypeRadioGroup'
import { HHFormSelectField, HHFormNumberField } from 'components/form-fields/v5'
import { EVENT_INSTANCE_TYPES } from 'settings/constants/service'
import { ALL_DAYS, WEEK_OF_MONTH_IN_NUMBERS } from 'settings/constants/day'
import T from 'T'
import { HHAlert } from 'components/common/HHAlert'
import { Z_INDEX } from 'theme/zIndex'
import { validateNonWeeklyRecurrence } from '../utils/validationHelpers'

const EditServiceRecurrenceNonWeeklySelector = () => {
  const {
    control,
    watch,
    trigger,
    formState: { errors },
  } = useFormContext()
  const theme = useTheme()

  const eventInstanceType = watch('eventInstanceType')
  const recurrenceFrequency = watch('recurrenceFrequency')
  const eventOccurrence = watch('eventOccurrence')
  const dayOfTheWeek = watch('dayOfTheWeek')
  const dayOfTheMonth = watch('dayOfTheMonth')

  const isDayOfWeekType = eventInstanceType === EVENT_INSTANCE_TYPES.DAY_OF_WEEK
  const isSpecificDateType = eventInstanceType === EVENT_INSTANCE_TYPES.SPECIFIC_DATE

  // Use the actual array length based on the event instance type
  const numberOfOccurrences = (() => {
    if (isDayOfWeekType && Array.isArray(dayOfTheWeek)) {
      return dayOfTheWeek.length
    }
    if (isSpecificDateType && Array.isArray(dayOfTheMonth)) {
      return dayOfTheMonth.length
    }
    return parseInt(recurrenceFrequency, 10) || 1
  })()

  const validationError = errors?.eventInstanceType?.message

  const getErrorMessage = (fieldBase, index) => {
    return get(errors, `${fieldBase}[${index}].message`)
  }
  const getWeekOptionLabel = useCallback(({ value }) => value, [])
  const getWeekOptionValue = useCallback(({ key }) => key, [])
  const getDayOptionValue = useCallback(v => v, [])
  const getDayOptionLabel = useCallback(l => l, [])

  const validateEventInstanceType = value => {
    const validation = validateNonWeeklyRecurrence(
      value,
      eventOccurrence,
      dayOfTheWeek,
      dayOfTheMonth,
      parseInt(recurrenceFrequency, 10) || ''
    )
    return validation.isValid ? true : validation.message
  }

  // Create stable keys for change detection
  const eventOccurrenceKey = Array.isArray(eventOccurrence) ? eventOccurrence.join('|') : eventOccurrence || ''
  const dayOfTheWeekKey = Array.isArray(dayOfTheWeek) ? dayOfTheWeek.join('|') : dayOfTheWeek || ''
  const dayOfTheMonthKey = Array.isArray(dayOfTheMonth) ? dayOfTheMonth.join('|') : dayOfTheMonth || ''

  const handleValidationTrigger = useCallback(() => {
    if (eventInstanceType && recurrenceFrequency) {
      trigger(['eventInstanceType', 'eventOccurrence', 'dayOfTheWeek', 'dayOfTheMonth'])
    }
  }, [eventInstanceType, recurrenceFrequency, trigger])

  const SelectProps = {
    MenuProps: {
      sx: {
        zIndex: Z_INDEX.MENU(theme),
      },
    },
  }

  useEffect(() => {
    handleValidationTrigger()
  }, [eventInstanceType, recurrenceFrequency, eventOccurrenceKey, dayOfTheWeekKey, dayOfTheMonthKey, handleValidationTrigger])

  return (
    <Box>
      {validationError && <HHAlert severity="error">{validationError}</HHAlert>}

      <Grid container size={12} mb={2}>
        <Controller
          name="eventInstanceType"
          control={control}
          rules={{
            required: T.EVENT_INSTANCE_REQUIRED,
            validate: validateEventInstanceType,
          }}
          render={({ field }) => <EventInstanceTypeRadioGroup {...field} showError={false} />}
        />
      </Grid>

      {numberOfOccurrences > 1 && (
        <Grid container size={12} mb={2}>
          <Typography variant="body2" color="text.secondary">
            Configure {numberOfOccurrences} occurrences per interval:
          </Typography>
        </Grid>
      )}

      {isDayOfWeekType && (
        <Box>
          {Array.from({ length: numberOfOccurrences }, (_, index) => (
            <Grid container spacing={2} mb={2} key={index}>
              {numberOfOccurrences > 1 && (
                <Grid size={12}>
                  <Typography variant="body2" fontWeight="medium" color="text.primary">
                    {T.EVENT_OCCURRENCE} {index + 1}
                  </Typography>
                </Grid>
              )}
              <Grid size={6}>
                <HHFormSelectField
                  getOptionLabel={getWeekOptionLabel}
                  getOptionValue={getWeekOptionValue}
                  fullWidth
                  control={control}
                  label={T.EVENT_OCCURRENCE}
                  name={`eventOccurrence[${index}]`}
                  rules={{
                    required: T.THIS_FIELD_IS_REQUIRED,
                  }}
                  error={Boolean(getErrorMessage('eventOccurrence', index))}
                  helperText={getErrorMessage('eventOccurrence', index)}
                  deprecatedLabel={false}
                  options={WEEK_OF_MONTH_IN_NUMBERS}
                  size="small"
                  SelectProps={SelectProps}
                />
              </Grid>
              <Grid size={6}>
                <HHFormSelectField
                  fullWidth
                  control={control}
                  name={`dayOfTheWeek[${index}]`}
                  rules={{
                    required: T.THIS_FIELD_IS_REQUIRED,
                  }}
                  label={T.DAY_OF_THE_WEEK}
                  deprecatedLabel={false}
                  options={ALL_DAYS}
                  getOptionValue={getDayOptionValue}
                  getOptionLabel={getDayOptionLabel}
                  error={Boolean(getErrorMessage('dayOfTheWeek', index))}
                  helperText={getErrorMessage('dayOfTheWeek', index)}
                  size="small"
                  SelectProps={SelectProps}
                />
              </Grid>
            </Grid>
          ))}
        </Box>
      )}

      {isSpecificDateType && (
        <Box>
          {Array.from({ length: numberOfOccurrences }, (_, index) => (
            <Grid container spacing={2} mb={2} key={index}>
              {numberOfOccurrences > 1 && (
                <Grid size={12}>
                  <Typography variant="body2" fontWeight="medium" color="text.primary">
                    Occurrence {index + 1}
                  </Typography>
                </Grid>
              )}
              <Grid size={12}>
                <HHFormNumberField
                  deprecatedLabel={false}
                  label={T.DAY_OF_THE_MONTH}
                  rules={{
                    required: T.THIS_FIELD_IS_REQUIRED,
                    min: {
                      value: 1,
                      message: T.DAY_FIELD_VALIDATION_ERROR,
                    },
                    max: {
                      value: 31,
                      message: T.DAY_FIELD_VALIDATION_ERROR,
                    },
                  }}
                  fullWidth
                  control={control}
                  name={`dayOfTheMonth[${index}]`}
                  error={Boolean(getErrorMessage('dayOfTheMonth', index))}
                  helperText={getErrorMessage('dayOfTheMonth', index)}
                  size="small"
                  showErrorMessage
                  SelectProps={SelectProps}
                />
              </Grid>
            </Grid>
          ))}
        </Box>
      )}
    </Box>
  )
}

export default EditServiceRecurrenceNonWeeklySelector
